import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const selectors = {
  DIALOG: 'dialog',
  HEADER: '.dialog-drawer__header',
  DIALOG_COMPONENT: 'dialog-component',
  HEADER_STICKY: 'dialog-drawer__header--sticky',
};

/**
 * A custom element that manages a cart drawer.
 * Delegates to the nested dialog-component for show/close; uses querySelector for dialog
 * since refs do not cross component boundaries.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  #abort = {
    /** @type {AbortController | null} */
    dialog: null,
    /** @type {AbortController | null} */
    scroll: null,
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.#abort.dialog = new AbortController();
    this.querySelector(selectors.DIALOG_COMPONENT)?.addEventListener(
      DialogOpenEvent.eventName,
      () => this.#updateStickyState(),
      { signal: this.#abort.dialog.signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#abort.dialog?.abort();
    this.#abort.scroll?.abort();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /** @returns {import('@theme/dialog').DialogComponent | null} */
  get #dialogComponent() {
    return this.querySelector(selectors.DIALOG_COMPONENT);
  }

  showDialog() {
    this.#dialogComponent?.showDialog?.();
  }

  closeDialog = () => {
    this.#dialogComponent?.closeDialog?.();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const dialog = this.querySelector(selectors.DIALOG);
    if (!dialog) return;

    this.#abort.scroll?.abort();
    this.#abort.scroll = new AbortController();

    const header = dialog.querySelector(selectors.HEADER);
    if (!header) return;

    dialog.addEventListener(
      'scroll',
      () => header.classList.toggle(selectors.HEADER_STICKY, dialog.scrollTop > 0),
      { signal: this.#abort.scroll.signal },
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
